/**
 * Предустановка: Полный Git Flow
 * Используется для уроков по Git Flow и методологиям ветвления
 */

import type { GitPreset } from './types';

/** Проект с полным Git Flow для демонстрации методологии */
export const gitFlowPreset: GitPreset = {
  id: 'git-flow',
  name: 'Git Flow проект',
  description:
    'Проект с полным Git Flow (main, develop, feature, release, hotfix) для изучения методологий ветвления',

  currentBranch: 'develop',

  branches: [
    { name: 'main', head: 'c1', color: '#F05032' },
    { name: 'develop', head: 'c8', color: '#4a9eff' },
    { name: 'feature/user-auth', head: 'c6', color: '#34d399' },
    { name: 'feature/api-client', head: 'c8', color: '#8b5cf6' },
    { name: 'release/1.0.0', head: 'c10', color: '#fbbf24' },
    { name: 'hotfix/critical-bug', head: 'c12', color: '#ef4444' },
  ],

  commits: [
    // Main branch
    {
      sha: 'c1',
      message: 'Initial commit',
      parents: [],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704067200, // 2024-01-01
      files: ['README.md', 'package.json'],
    },
    // Hotfix from main
    {
      sha: 'c11',
      message: 'Hotfix: Fix critical security vulnerability',
      parents: ['c1'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704240000, // 2024-01-03
      files: ['src/security.ts'],
    },
    {
      sha: 'c12',
      message: 'Hotfix: Update version to 1.0.1',
      parents: ['c11'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704326400, // 2024-01-04
      files: ['package.json'],
    },
    // Develop branch
    {
      sha: 'c2',
      message: 'Create develop branch with initial structure',
      parents: ['c1'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704067200, // 2024-01-01
      files: ['src/index.ts', 'src/config.ts'],
    },
    {
      sha: 'c3',
      message: 'Add core modules',
      parents: ['c2'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704153600, // 2024-01-02
      files: ['src/core.ts', 'src/types.ts'],
    },
    // Feature: user-auth
    {
      sha: 'c4',
      message: 'Start user authentication feature',
      parents: ['c3'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704240000, // 2024-01-03
      files: ['src/auth.ts'],
    },
    {
      sha: 'c5',
      message: 'Add login and registration',
      parents: ['c4'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704326400, // 2024-01-04
      files: ['src/auth.ts', 'src/user.ts'],
    },
    {
      sha: 'c6',
      message: 'Complete user authentication',
      parents: ['c5'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704412800, // 2024-01-05
      files: ['src/session.ts', 'tests/auth.test.ts'],
    },
    // Merge feature/user-auth to develop
    {
      sha: 'c7',
      message: 'Merge feature/user-auth into develop',
      parents: ['c3', 'c6'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704499200, // 2024-01-06
      files: [],
    },
    // Feature: api-client
    {
      sha: 'c8',
      message: 'Add API client with retry logic',
      parents: ['c7'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704585600, // 2024-01-07
      files: ['src/api.ts', 'src/retry.ts'],
    },
    // Release branch
    {
      sha: 'c9',
      message: 'Prepare release 1.0.0',
      parents: ['c8'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704672000, // 2024-01-08
      files: ['CHANGELOG.md', 'package.json'],
    },
    {
      sha: 'c10',
      message: 'Update version and documentation for release',
      parents: ['c9'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704758400, // 2024-01-09
      files: ['README.md', 'docs/'],
    },
  ],

  files: [
    {
      path: 'README.md',
      content: `# Enterprise Application

Полноценное приложение с Git Flow ветвлением.

## Ветки

- \`main\` - production код
- \`develop\` - разработка
- \`feature/*\` - новые функции
- \`release/*\` - подготовка релизов
- \`hotfix/*\` - срочные исправления

## Установка

\`\`\`bash
npm install
\`\`\`

## Запуск

\`\`\`bash
npm start
\`\`\`
`,
      status: 'committed',
    },
    {
      path: 'package.json',
      content: `{
  "name": "enterprise-app",
  "version": "1.0.1",
  "description": "Enterprise application with Git Flow",
  "main": "src/index.ts",
  "scripts": {
    "start": "ts-node src/index.ts",
    "test": "jest",
    "build": "tsc"
  },
  "author": "Ivan Petrov",
  "license": "MIT"
}
`,
      status: 'committed',
    },
    {
      path: 'src/index.ts',
      content: `import { createApp } from './core';
import { config } from './config';

const app = createApp(config);

app.start().then(() => {
  console.log('Application started successfully');
}).catch((error) => {
  console.error('Failed to start:', error);
  process.exit(1);
});
`,
      status: 'committed',
    },
    {
      path: 'src/config.ts',
      content: `export const config = {
  apiUrl: 'https://api.example.com',
  timeout: 5000,
  retries: 3,
  debug: process.env.NODE_ENV === 'development',
};
`,
      status: 'committed',
    },
    {
      path: 'src/core.ts',
      content: `import type { Config } from './types';
import { AuthService } from './auth';
import { ApiClient } from './api';

export function createApp(config: Config) {
  const auth = new AuthService(config);
  const api = new ApiClient(config);

  return {
    config,
    auth,
    api,

    async start() {
      console.log('Starting application...');
      await this.initialize();
    },

    async initialize() {
      await auth.initialize();
      await api.initialize();
      console.log('Application initialized');
    }
  };
}
`,
      status: 'committed',
    },
    {
      path: 'src/types.ts',
      content: `export interface Config {
  apiUrl: string;
  timeout: number;
  retries: number;
  debug: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
}

export interface AuthResult {
  success: boolean;
  token?: string;
  error?: string;
}
`,
      status: 'committed',
    },
    {
      path: 'src/auth.ts',
      content: `import type { Config, AuthResult, User } from './types';
import { SessionManager } from './session';

export class AuthService {
  private session: SessionManager;

  constructor(private config: Config) {
    this.session = new SessionManager();
  }

  async initialize() {
    console.log('Auth service initialized');
  }

  async login(email: string, password: string): Promise<AuthResult> {
    if (password.length < 6) {
      return { success: false, error: 'Invalid credentials' };
    }

    const token = this.session.create(email);
    return { success: true, token };
  }

  async logout(token: string): Promise<void> {
    this.session.destroy(token);
  }

  async validate(token: string): Promise<User | null> {
    const userId = this.session.get(token);
    if (!userId) return null;

    return {
      id: userId,
      email: userId,
      name: 'User',
    };
  }
}
`,
      status: 'committed',
    },
    {
      path: 'src/session.ts',
      content: `export class SessionManager {
  private sessions: Map<string, { userId: string; expiresAt: Date }> = new Map();

  create(userId: string): string {
    const token = crypto.randomUUID();
    const expiresAt = new Date(Date.now() + 3600000); // 1 hour

    this.sessions.set(token, { userId, expiresAt });
    return token;
  }

  get(token: string): string | null {
    const session = this.sessions.get(token);
    if (!session || session.expiresAt < new Date()) {
      return null;
    }
    return session.userId;
  }

  destroy(token: string): void {
    this.sessions.delete(token);
  }
}
`,
      status: 'committed',
    },
    {
      path: 'src/user.ts',
      content: `import type { User } from './types';

export class UserService {
  private users: Map<string, User> = new Map();

  async create(email: string, name: string, password: string): Promise<User> {
    const user: User = {
      id: crypto.randomUUID(),
      email,
      name,
    };

    this.users.set(user.id, user);
    return user;
  }

  async findById(id: string): Promise<User | null> {
    return this.users.get(id) ?? null;
  }

  async findByEmail(email: string): Promise<User | null> {
    for (const user of this.users.values()) {
      if (user.email === email) return user;
    }
    return null;
  }
}
`,
      status: 'committed',
    },
    {
      path: 'src/api.ts',
      content: `import type { Config } from './types';
import { RetryManager } from './retry';

export class ApiClient {
  private retry: RetryManager;

  constructor(private config: Config) {
    this.retry = new RetryManager(config.retries);
  }

  async initialize() {
    console.log('API client initialized');
  }

  async get<T>(path: string): Promise<T> {
    return this.retry.execute(async () => {
      const response = await fetch(\`\${this.config.apiUrl}\${path}\`, {
        timeout: this.config.timeout,
      });
      return response.json();
    });
  }

  async post<T>(path: string, data: unknown): Promise<T> {
    return this.retry.execute(async () => {
      const response = await fetch(\`\${this.config.apiUrl}\${path}\`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      return response.json();
    });
  }
}
`,
      status: 'committed',
    },
    {
      path: 'src/retry.ts',
      content: `export class RetryManager {
  constructor(private maxRetries: number) {}

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error as Error;
        if (attempt < this.maxRetries) {
          await this.delay(1000 * Math.pow(2, attempt));
        }
      }
    }

    throw lastError;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
`,
      status: 'committed',
    },
    {
      path: 'src/security.ts',
      content: `/**
 * Security utilities for input validation and sanitization
 */

export function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '')
    .replace(/javascript:/gi, '')
    .trim();
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function hashPassword(password: string): string {
  // В реальном приложении используйте bcrypt или argon2
  return \`hashed_\${password}_\${Date.now()}\`;
}

export function comparePassword(password: string, hash: string): boolean {
  // В реальном приложении используйте bcrypt.compare
  return hash.includes(password);
}
`,
      status: 'committed',
    },
    {
      path: 'tests/auth.test.ts',
      content: `import { AuthService } from '../src/auth';

describe('AuthService', () => {
  let auth: AuthService;

  beforeEach(() => {
    auth = new AuthService({
      apiUrl: 'http://test',
      timeout: 1000,
      retries: 1,
      debug: false,
    });
  });

  test('should login with valid credentials', async () => {
    const result = await auth.login('test@example.com', 'password123');
    expect(result.success).toBe(true);
    expect(result.token).toBeDefined();
  });

  test('should reject short passwords', async () => {
    const result = await auth.login('test@example.com', 'short');
    expect(result.success).toBe(false);
    expect(result.error).toBe('Invalid credentials');
  });
});
`,
      status: 'committed',
    },
    {
      path: 'CHANGELOG.md',
      content: `# Changelog

## [1.0.0] - 2024-01-09

### Added
- User authentication with login/logout
- API client with retry logic
- Session management
- Security utilities

### Changed
- Improved error handling
- Updated documentation

### Fixed
- Critical security vulnerability (hotfix 1.0.1)
`,
      status: 'committed',
    },
  ],

  visualization: {
    branchColors: {
      main: '#F05032',
      develop: '#4a9eff',
      'feature/user-auth': '#34d399',
      'feature/api-client': '#8b5cf6',
      'release/1.0.0': '#fbbf24',
      'hotfix/critical-bug': '#ef4444',
    },
    nodePositions: {
      c1: { x: 100, y: 100 },
      c11: { x: 200, y: 100 },
      c12: { x: 300, y: 100 },
      c2: { x: 100, y: 200 },
      c3: { x: 200, y: 200 },
      c4: { x: 300, y: 300 },
      c5: { x: 400, y: 300 },
      c6: { x: 500, y: 300 },
      c7: { x: 500, y: 200 },
      c8: { x: 600, y: 200 },
      c9: { x: 700, y: 250 },
      c10: { x: 800, y: 250 },
    },
    annotations: [
      { sha: 'c1', text: 'main', position: 'top' },
      { sha: 'c8', text: 'develop', position: 'top' },
      { sha: 'c6', text: 'feature', position: 'right' },
      { sha: 'c10', text: 'release', position: 'right' },
      { sha: 'c12', text: 'hotfix', position: 'right' },
    ],
    highlightedCommits: ['c7', 'c12'],
  },
};
